import { Box } from "@mui/material"
import { useCallback, useEffect, useRef, useState } from "react"
import { atom, useRecoilState, useSetRecoilState } from "recoil"
import { useUser } from "../atoms"
import { useMyHistory } from "../atoms/portale"
import Constants from "../Constants"
import MyHistory from "../models/MyHistory"
import { NullableString, Preferito, ServiceDescriptor } from "../models/ServiceDescriptor"
import { CodiciApplType } from "../pages/DoActivityAreaLevelPage"
import apiService from "../services/api"
// import logConstants from "../utils/logConstants"
import BPointWebForm, { BPointWebParamsType } from "./BPointWebForm"
import CodiciModal from "./CodiciModal"

type ServiceDescriptorOrPreferitoType = {
    sdOrPref: ServiceDescriptor | Preferito,
    isPreferito: boolean,
    history?: MyHistory,
}

export const applToStartAtom = atom<ServiceDescriptorOrPreferitoType | undefined>({
    key: 'applToStartAtom',
    default: undefined
})

export const useStartApplication = () => {
    const setStartApplication = useSetRecoilState(applToStartAtom)
    return (sd: ServiceDescriptor | Preferito, isPreferito?: boolean) => {
        // console.log(`Start Application: ${isPreferito ? (sd as Preferito).serviceDescriptor?.urlDescription : (sd as ServiceDescriptor).urlDescription} [${sd.id}] ${!!isPreferito && 'Preferito'}`)
        setStartApplication({ sdOrPref: sd, isPreferito: !!isPreferito })
    }
}

export const useReuseHistory = () => {
    const setStartApplication = useSetRecoilState(applToStartAtom)
    return (history: MyHistory) => {
        const sd = history.serviceDescriptor
        // console.log(`%cStart Application (reuse History): ${sd.urlDescription} [${sd.id}], HistoryContext: ${history.contesto}`, logConstants.magenta14ptBold)
        setStartApplication({ sdOrPref: sd, isPreferito: false, history })
    }
}

const StartApplication = () => {
    const [startApplication, setStartApplication] = useRecoilState(applToStartAtom)
    const [bPointParams, setBPointParams] = useState<BPointWebParamsType>()
    const [codiciAppl, setCodiciAppl] = useState<CodiciApplType>({ open: false })
    const user = useUser()
    const { refresh: historyRefresh } = useMyHistory()

    const applHrefRef = useRef<HTMLAnchorElement>(null)

    const handleOnHide = () => setCodiciAppl({ open: false, appl: undefined, uffi: undefined, label: undefined, invite: undefined })
    const startBPoint = useCallback((sd: ServiceDescriptor, xSixContext: NullableString) => {
        if(!xSixContext) return
        apiService.getBPointStartParams(sd.id, sd.livello, xSixContext)
            .then(params => {
                // console.log(`%cBPoint: xSixContext: ${xSixContext} sd: %o`, logConstants.red14ptBold, sd)
                if (params) {
                    if (params.T7 === "WEB") {
                        setBPointParams(params)
                    } else if (params.T7 === "WKITERM" && applHrefRef.current) {
                        // console.log(`href: ${params.href}`)
                        applHrefRef.current.href = params.href
                        applHrefRef.current.click()
                    }
                }
                historyRefresh(1000)
            })
    }, [historyRefresh])

    useEffect(() => { bPointParams && setBPointParams(undefined) }, [bPointParams])

    useEffect(() => {
        const handleStartLink = (preferito: Preferito) => {
            setStartApplication(undefined)
            if (applHrefRef.current !== null) {
                applHrefRef.current.href = `${Constants.apiUrl}/start/link?id=${preferito.id}`
                applHrefRef.current.click()
            }
        }
        const handleStartAppl = (servDesc: ServiceDescriptor, preferito?: Preferito, contesto?: string) => {
            const sd = { ...servDesc }
            setStartApplication(undefined)
            console.log('start appl')
            const userctx = user.context;
            // console.log(`handleStartAppl: ${userctx?.xSixContext} ${userctx?.ufficioCorrente} ${sd.livello}`, sd)
            // Se il tipo di applicazione prevede dei codici provvediamo con una modal
            if ((!!!preferito?.contesto) && (!!!contesto) && sd.tipologiaSito === 'c') {
                sd.guid && setCodiciAppl({ open: true, appl: sd })
            } else if (sd.tipoAppl === 'Osra' && startApplication?.history) {
                // console.log('%cOsra application, from history', logConstants.blue12ptBold)
                startBPoint(sd, startApplication.history.contesto)
            } else if (sd.tipoAppl === 'Osra') {
                //console.log('Osra application, compute operatori')
                sd.guid && userctx && setCodiciAppl({ open: true, appl: sd, uffi: userctx.xSixContext, invite: 'Seleziona Operatore', label: 'Operatore selezionato' })
                //console.log('ho eseguito setCodici')
            } else {
                // Impostazione di HREF e click del link
                console.log('Start all the other')
                if (applHrefRef.current !== null) {
                    if (preferito) {
                        applHrefRef.current.href = `${Constants.apiUrl}/start/pref?id=${preferito.id}&lv=${sd.livello}&ctx=${userctx?.xSixContext}`
                    } else {
                        applHrefRef.current.href = `${Constants.apiUrl}/start/appl?id=${sd.id}&lv=${sd.livello}&ctx=${contesto ?? userctx?.xSixContext}`
                    }
                    //console.log(`Start Url: ${applHrefRef.current.href}`)
                    applHrefRef.current.click()
                    historyRefresh()
                }
            }
            // console.log('Esco dalla funzione')
        }
        // startApplication && console.log(`useEffect on StartApplication`, startApplication)
        if (startApplication) {
            if (startApplication.isPreferito) {
                const pref = startApplication.sdOrPref as Preferito
                if (pref.serviceDescriptor) {
                    handleStartAppl(pref.serviceDescriptor, pref)
                } else {
                    handleStartLink(pref)
                }
            } else {
                if (startApplication.history) {
                    handleStartAppl(startApplication.sdOrPref as ServiceDescriptor, undefined, startApplication.history.contesto ?? undefined)
                } else {
                    handleStartAppl(startApplication.sdOrPref as ServiceDescriptor)
                }
            }
        }
    }, [historyRefresh, setStartApplication, startApplication, startBPoint, user.context])

    const handleCodiceSelected = (sd: ServiceDescriptor, codice: string) => {
        const userctx = user.context;
        // console.log(`Codice selected ${codice} - xSixContext: ${userctx?.xSixContext}`)
        if (applHrefRef.current !== null && userctx) {
            let xSixContext = userctx.xSixContext
            if (sd.tipoAppl === "Osra") {
                xSixContext = `O${xSixContext};${codice}`
                startBPoint(sd, xSixContext)
            } else {
                const prov = userctx.xSixContext.split(';')[2]
                xSixContext = `C;${codice};${prov}`
                applHrefRef.current.href = `${Constants.apiUrl}/start/appl?id=${sd.id}&lv=${sd.livello}&ctx=${xSixContext}`
                applHrefRef.current.click()
            }
        }
        handleOnHide()
        historyRefresh(2000)
    }

    return (
        <>
            <Box sx={{ position: 'fixed', zIndex: 0, top: 0, left: 0, backgroundColor: 'transparent', color: 'transparent' }}>
                {bPointParams && <BPointWebForm {...bPointParams} />}
                <a href="http://google.com" target="_blank" rel="noreferrer noopener" ref={applHrefRef}>.</a>
            </Box>
            <CodiciModal
                open={codiciAppl.open}
                onHide={handleOnHide}
                onSelected={handleCodiceSelected}
                appl={codiciAppl.appl}
                invite={codiciAppl.invite}
                label={codiciAppl.label}
            />
        </>
    )
}

export default StartApplication